import api, { toUserError } from './api'
import { normalizeAdditionalSpecs } from '../utils/specs'
import { normalizePartProjects } from './projectApi'

export function normalizePart(part) {
  if (!part) return null
  return {
    ...part,
    minStock: part.minimumStock ?? part.minStock ?? 0,
    specs: part.description || part.specs || '',
    note: part.memo || part.note || '',
    quantityNeedsVerification: Boolean(part.quantityNeedsVerification),
    modelNeedsVerification: Boolean(part.modelNeedsVerification),
    size: part.size || '',
    storageMethod: part.storageMethod || '',
    location: part.location || '',
    modelMatchStatus: part.modelMatchStatus || '',
    mergedIntoPartId: part.mergedIntoPartId ? String(part.mergedIntoPartId) : '',
    mergedIntoPartCode: part.mergedIntoPartCode || '',
    mergedAt: part.mergedAt || null,
    mergedBy: part.mergedBy ? String(part.mergedBy) : '',
    projects: normalizePartProjects(part.projects),
  }
}

export function normalizeTransaction(tx) {
  if (!tx) return null
  const type = String(tx.type || '').toUpperCase()
  return {
    ...tx,
    type,
    model: tx.model || tx.part?.model || '',
    manufacturer: tx.manufacturer || tx.part?.manufacturer || '',
    name: tx.name || tx.part?.name || '',
    partCode: tx.partCode || tx.part?.partCode || '',
    note: tx.memo || tx.note || '',
    beforeQuantity: tx.beforeQuantity ?? tx.previousQuantity ?? 0,
    afterQuantity: tx.afterQuantity ?? tx.balanceAfter ?? 0,
  }
}

export async function fetchPartsPage(params = {}) {
  try {
    const query = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value == null || value === '' || value === 'all') return
      query[key] = value
    })
    const { data } = await api.get('/parts', { params: query })
    const payload = data.data || {}
    const items = (payload.items || payload.parts || []).map(normalizePart)
    const total = Number(payload.total ?? payload.count ?? items.length) || 0
    const limit = Number(payload.limit) || items.length || 20
    const page = Number(payload.page) || 1
    const totalPages = Number(payload.totalPages) || Math.max(1, Math.ceil(total / (limit || 1)))
    return {
      items,
      total,
      page,
      limit,
      totalPages,
      reviewCounts: payload.reviewCounts || null,
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function fetchParts(params = {}) {
  const result = await fetchPartsPage(params)
  return result.items
}

export async function fetchPart(id) {
  try {
    const { data } = await api.get(`/parts/${id}`)
    return normalizePart(data.data?.part)
  } catch (error) {
    throw toUserError(error)
  }
}

export async function createPart(payload) {
  try {
    const { data } = await api.post('/parts', payload)
    return {
      part: normalizePart(data.data?.part),
      openingTransaction: data.data?.openingTransaction
        ? normalizeTransaction(data.data.openingTransaction)
        : null,
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function updatePart(id, payload) {
  try {
    const { data } = await api.put(`/parts/${id}`, payload)
    return normalizePart(data.data?.part)
  } catch (error) {
    throw toUserError(error)
  }
}

export async function fetchTransactionsPage(params = {}) {
  try {
    const query = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value == null || value === '' || value === 'all') return
      query[key] = value
    })
    const { data } = await api.get('/transactions', { params: query })
    const payload = data.data || {}
    const items = (payload.items || payload.transactions || []).map(normalizeTransaction)
    const total = Number(payload.total ?? payload.count ?? items.length) || 0
    const limit = Number(payload.limit) || items.length || 20
    const page = Number(payload.page) || 1
    const totalPages = Number(payload.totalPages) || Math.max(1, Math.ceil(total / (limit || 1)))
    return { items, total, page, limit, totalPages }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function fetchTransactions(params = {}) {
  const result = await fetchTransactionsPage(params)
  return result.items
}

export async function stockIn(payload) {
  try {
    const { data } = await api.post('/transactions/in', payload)
    return {
      part: normalizePart(data.data?.part),
      transaction: normalizeTransaction(data.data?.transaction),
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function stockOut(payload) {
  try {
    const { data } = await api.post('/transactions/out', payload)
    return {
      part: normalizePart(data.data?.part),
      transaction: normalizeTransaction(data.data?.transaction),
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function stockAdjust(payload) {
  try {
    const { data } = await api.post('/transactions/adjust', payload)
    return {
      part: normalizePart(data.data?.part),
      transaction: normalizeTransaction(data.data?.transaction),
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function mergePart(sourceId, targetPartId) {
  try {
    const { data } = await api.post(`/parts/${sourceId}/merge`, { targetPartId })
    return {
      source: normalizePart(data.data?.source),
      target: normalizePart(data.data?.target),
      transactions: {
        source: data.data?.transactions?.source ? normalizeTransaction(data.data.transactions.source) : null,
        target: data.data?.transactions?.target ? normalizeTransaction(data.data.transactions.target) : null,
      },
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function deletePart(id) {
  try {
    const { data } = await api.delete(`/parts/${id}`)
    return {
      id: data.data?.id ? String(data.data.id) : String(id),
      partCode: data.data?.partCode || '',
      model: data.data?.model || '',
      name: data.data?.name || '',
      message: data.message || '품목이 삭제되었습니다.',
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function reviewPart(id, payload) {
  try {
    const { data } = await api.post(`/parts/${id}/review`, payload)
    return {
      part: normalizePart(data.data?.part),
      transaction: data.data?.transaction ? normalizeTransaction(data.data.transaction) : null,
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export async function fetchDashboardSummary() {
  try {
    const { data } = await api.get('/dashboard/summary')
    const summary = data.data || {}
    return {
      ...summary,
      lowParts: (summary.lowParts || []).map(normalizePart),
      recentTransactions: (summary.recentTransactions || []).map(normalizeTransaction),
    }
  } catch (error) {
    throw toUserError(error)
  }
}

export function toPartPayload(form, { includeQuantity = false } = {}) {
  const payload = {
    category: form.category,
    manufacturer: form.manufacturer,
    model: form.model,
    name: form.name,
    description: form.description || form.specs || '',
    powerType: form.powerType,
    poles: form.poles,
    ratedCurrent: form.ratedCurrent,
    ratedVoltage: form.ratedVoltage,
    breakingCapacity: form.breakingCapacity,
    power: form.power,
    contactConfiguration: form.contactConfiguration,
    coilVoltage: form.coilVoltage,
    communication: form.communication,
    dimensions: form.dimensions,
    additionalSpecs: normalizeAdditionalSpecs(form.additionalSpecs),
    minimumStock: form.minimumStock ?? form.minStock,
    unit: form.unit,
    status: form.status || form.usageStatus || 'ACTIVE',
    verificationStatus: form.verificationStatus || 'UNVERIFIED',
    inventoryNote: form.inventoryNote || '',
    quantityNeedsVerification: Boolean(form.quantityNeedsVerification),
    modelNeedsVerification: Boolean(form.modelNeedsVerification),
    memo: form.memo ?? form.note ?? '',
    projectIds: normalizePartProjects(form.projects).map((item) => item.projectId),
  }
  if (includeQuantity) payload.quantity = form.quantity
  return payload
}
